import { useQuery } from '@apollo/client';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import PlaceholderContainer from '@nile/banyan/src/Containers/ListContainers/PlaceholderContainer';
import { GET_NOTIFICATION_TEMPLATE_META_INFO } from '@nile/banyan/src/Graphql/Mutations/templates';
import { MentionsInput, Mention } from 'react-mentions';
import {
  TextField,
  Banner,
  Button,
  ButtonGroup,
  DisplayText
} from '@superops/ui-design-system';
import React, { useState, useEffect, useCallback } from 'react';
import {
  EmailSenderForm,
  NotificationFilterForm
} from '@nile/banyan/src/Components/Forms';
import {
  TEMPLATE_SUB_TYPES,
  TEMPLATE_TYPES,
  TICKETING_REQUESTER_TEMPLATES,
  TICKETING_TECHNICIAN_TEMPLATES
} from '@nile/banyan/src/Constants/notifications';
import { getSubjectPlaceholdersObj } from '@nile/banyan/src/Pebbles/subjectPlaceholders';
import { reactSelectInlineError } from '@nile/banyan/src/Utils/style';
import { preFormatDataForMentionsLibrary } from '@nile/client_management/src/middleware/InvoiceEmailTemplate/invoiceEmailTemplate';
import {
  getTranslatedText,
  TEXT_LENGTH,
  mentionsContainerStyle,
  mentionsItemStyle,
  getHeaderValue,
  Loader,
  fetchApi,
  Editor
} from '@nile/components';
import {
  AccountClient,
  DELETE_NOTIFICATION_TEMPLATE_ATTACHMENT,
  GET_ENTITY_PROPERTIES
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { cloneDeep } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

const ReplyTemplateContainer = styled.div`
  // min-height: ${(props) =>
    props.type === TEMPLATE_TYPES.reply
      ? 'calc(100vh - 330px)'
      : 'calc(100vh - 590px)'};

  #quill-rt-editor {
    height: 100%;
    .quill {
      min-height: 400px;
      .ql-container {
        border: none;
        min-height: inherit;
        .ql-editor {
          min-height: inherit;
          padding: 0px;
        }
      }
    }
    .ql-toolbar {
      border: none;
    }
  }
`;

const MentionsHolder = styled.div`
  input {
    &:focus {
      border: 2px inset transparent;
      outline: none;
    }
  }
`;

const ALLOW_FILTER_OPTIONS = [
  TICKETING_REQUESTER_TEMPLATES.ticketCreated,
  TICKETING_TECHNICIAN_TEMPLATES.ticketCreated
];

function EmailTemplateEditor(props) {
  const {
    intl,
    template,
    saveContent,
    updateTemplate,
    module,
    details,
    hideSubject,
    validationError,
    errors,
    updateName,
    editorRef,
    disableAttachment,
    disableImageUpload,
    allowAttachments
  } = props;

  const [value, setValue] = useState(template?.subject || '');
  const [data, setData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isBannerVisible, showBanner] = useState(true);

  const [placeholderFields, setPlaceholderFields] = useState([]);

  const getRelatedEntityAttributes = (entityMeta, eType) => {
    return entityMeta.find((el) => el.name === eType)?.attributes || [];
  };

  const handleEntityCompleted = useCallback(
    (entityFields) => {
      AccountClient.query({
        query: GET_NOTIFICATION_TEMPLATE_META_INFO,
        variables: {
          templateType: template?.templateType,
          templateSubtype: template?.templateSubtype,
          constant: template?.constant,
          channel: template?.channel
        }
      }).then((res) => {
        if (res.data?.getNotificationTemplateMetaInfo) {
          setData(res.data);
          if (
            template?.templateType === TEMPLATE_TYPES.systemClient ||
            template?.templateType === TEMPLATE_TYPES.systemTask ||
            template?.templateType === TEMPLATE_TYPES.invoice ||
            template?.name === 'Technician Mention'
          ) {
            const placeholderSections = preFormatDataForMentionsLibrary(
              res.data.getNotificationTemplateMetaInfo.variables,
              intl
            );
            let placeholders = [];
            placeholderSections.forEach((el) => {
              placeholders = placeholders.concat(el.fields);
            });
            setPlaceholderFields(placeholders);
          } else {
            let phFields = [];
            const fieldsList = [];
            let dummy = res.data.getNotificationTemplateMetaInfo?.variables?.forEach(
              (type) =>
                type.fields.forEach((field) => {
                  fieldsList.push(field);
                })
            );

            const getParentEntityType = () => {
              if (template?.templateType === TEMPLATE_TYPES.systemOnboarding) {
                if (
                  template?.templateSubtype === TEMPLATE_SUB_TYPES.technician
                ) {
                  return 'employee';
                }
                if (
                  template?.templateSubtype === TEMPLATE_SUB_TYPES.requester
                ) {
                  return 'clientUser';
                }
                if (template?.templateSubtype === TEMPLATE_SUB_TYPES.user) {
                  return 'user';
                }
              }
              if (template?.templateType === TEMPLATE_TYPES.systemTicketing) {
                return 'ticket';
              }
            };

            const entityMeta = entityFields.getEntitiesMeta;
            const ticketEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Ticket'
            );
            const requesterEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Requester'
            );
            const technicianEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Technician'
            );
            const techGroupEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'TechnicianGroup'
            );
            const clientEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Client'
            );
            const siteEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Site'
            );
            const slaEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'SLA'
            );
            const employeeEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Employee'
            );
            const clientUserEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'ClientUser'
            );
            const designationEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Designation'
            );
            const businessFunctionEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'BusinessFunction'
            );
            const teamEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Team'
            );
            const roleEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Role'
            );
            const inviterEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'Inviter'
            );
            const mailSettingsEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'OutgoingMailSettings'
            );
            const currentApprovalEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'CurrentApproval'
            );
            const currentNoteEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'CurrentNote'
            );
            const currentReplyEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'CurrentReply'
            );
            const workItemEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'WorkItem'
            );
            const userEntityFields = getRelatedEntityAttributes(
              entityMeta,
              'User'
            );
            const typeMap = {
              ticket: ticketEntityFields,
              requester: requesterEntityFields,
              technician: technicianEntityFields,
              client: clientEntityFields,
              site: siteEntityFields,
              sla: slaEntityFields,
              techGroup: techGroupEntityFields,
              employee: employeeEntityFields,
              clientUser: clientUserEntityFields,
              designation: designationEntityFields,
              businessFunction: businessFunctionEntityFields,
              team: teamEntityFields,
              role: roleEntityFields,
              inviter: inviterEntityFields,
              mailSettings: mailSettingsEntityFields,
              currentApproval: currentApprovalEntityFields,
              currentNote: currentNoteEntityFields,
              currentReply: currentReplyEntityFields,
              workItem: workItemEntityFields,
              user: userEntityFields
            };
            const getAttributeData = (type = 'ticket', name) => {
              return typeMap?.[type].find((att) => att.name === name)
                ?.attributePlaceHolder;
            };
            phFields = fieldsList.map((field) => {
              if (field.includes('.')) {
                const fieldArr = field.split('.');
                const attrIndex = fieldArr?.length - 2;
                const valIndex = fieldArr?.length - 1;
                const attr = fieldArr?.[attrIndex];
                const val = fieldArr?.[valIndex];
                return {
                  ...getAttributeData(attr, val),
                  placeholderValue: field
                };
              }
              return {
                ...getAttributeData(getParentEntityType(), field),
                placeholderValue: field
              };
            });
            phFields = phFields.map((field) => ({
              ...field,
              text: getTranslatedText(intl, field.label),
              value: getTranslatedText(intl, field.label),
              url: field.key,
              id: field.key,
              display: getTranslatedText(intl, field.label),
              label: getTranslatedText(intl, field.label)
            }));
            setPlaceholderFields(phFields);
          }
        }
      });
    },
    [intl, template]
  );

  const { loading: entityLoading, data: entityData } = useQuery(
    GET_ENTITY_PROPERTIES,
    {
      client: AccountClient
    }
  );

  useEffect(() => {
    if (entityData) {
      handleEntityCompleted(entityData);
    }
  }, [template?.templateSubtype, entityData, handleEntityCompleted]);

  if (entityLoading) {
    return <Loader />;
  }

  const handleFileAttachments = (files, callback) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(`files`, file);
    });
    const detailObj = {};
    if (template?.id) {
      detailObj.notificationTemplateId = template.id;
    }
    if (template?.templateType) {
      detailObj.type = template.templateType;
    }
    formData.append('details', JSON.stringify(detailObj));

    formData.append('module', 'NOTIFICATION_TEMPLATE_ATTACHMENT');

    setIsUploading(true);
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => response.data)
      .then((list) => {
        const updatedList =
          [...cloneDeep(template?.attachmentList), ...list]?.map(
            ({ fileName, originalFileName, fileSize }) => {
              return { fileName, originalFileName, fileSize };
            }
          ) || [];

        updateTemplate({
          attachmentList: updatedList
        });
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsUploading(false);
        if (callback) {
          callback();
        }
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    if (template?.id) {
      AccountClient.mutate({
        mutation: DELETE_NOTIFICATION_TEMPLATE_ATTACHMENT,
        variables: {
          notificationTemplateType: template.templateType,
          notificationTemplateId: template.id,
          fileName
        }
      }).then((res) => {
        if (res?.data?.deleteNotificationTemplateAttachment) {
          const updatedList = cloneDeep(template?.attachmentList).filter(
            (item) => item.fileName !== fileName
          );
          updateTemplate({
            attachmentList: updatedList
          });
        }
      });
    } else {
      const updatedList = cloneDeep(template?.attachmentList).filter(
        (item) => item.fileName !== fileName
      );
      updateTemplate({ attachmentList: updatedList });
    }
  };

  const handleSubjectChange = (e, newVal, newPlainVal) => {
    setValue(newPlainVal || '');
    let updatedMentions = cloneDeep(template?.placeholdersUsed) || '{}';
    updatedMentions = JSON.parse(updatedMentions);
    const placeholders = getSubjectPlaceholdersObj(
      newPlainVal,
      placeholderFields
    );
    updatedMentions.subject = Object.keys(placeholders).reduce((acc, key) => {
      if (key.includes('udf')) {
        // Custom field
        acc[key] = placeholders[key];
        return acc;
      }
      acc[key] = getTranslatedText(intl, key);
      return acc;
    }, {});
    updatedMentions = JSON.stringify(updatedMentions);
    updateTemplate({ subject: newPlainVal, placeholdersUsed: updatedMentions });
  };

  return (
    <div
      className="fx white full-width full-height fx-sb no-scroll"
      hideSubject={hideSubject}
    >
      <div className="fx col template-left-panel w80 scroll">
        <div>
          {(props?.type === TEMPLATE_TYPES.reply ||
            props?.type === TEMPLATE_TYPES.sideConversation) && (
            <div superid="rtHeader" className="template-name mb-20">
              <TextField
                fontSize="fontLarge"
                noborder
                value={template.name}
                id={template.id}
                type="text"
                error={errors?.name}
                onChange={updateName}
                placeholder={getTranslatedText(
                  intl,
                  'admin.template.name.placeholder'
                )}
              />
            </div>
          )}

          {!hideSubject && (
            <MentionsHolder>
              <MentionsInput
                singleLine
                value={value}
                onChange={handleSubjectChange}
                placeholder={getTranslatedText(
                  intl,
                  template?.templateType === TEMPLATE_TYPES.invoice
                    ? 'invoice.reminder.placeholder'
                    : 'admin.eventTrigger.createNotif.subject'
                )}
                style={mentionsContainerStyle}
                maxLength={TEXT_LENGTH.size_7}
              >
                <Mention
                  trigger="#"
                  data={placeholderFields}
                  style={mentionsItemStyle}
                  displayTransform={(a, b) => `#${b}`}
                  appendSpaceOnAdd
                />
              </MentionsInput>
            </MentionsHolder>
          )}
          {validationError &&
            (validationError.subject || validationError.content) &&
            reactSelectInlineError(
              getTranslatedText(
                intl,
                props?.type === TEMPLATE_TYPES.reply
                  ? 'admin.eventTrigger.validation.content'
                  : 'admin.eventTrigger.validation.message'
              )
            )}
          <ReplyTemplateContainer
            className="flex white-grey-bg m-0 br-none"
            onClick={saveContent}
            type={template?.templateType}
            hasAttachments={
              template?.attachmentList && template?.attachmentList.length > 0
            }
          >
            <Editor
              focusEditor
              template={template}
              updateTemplate={updateTemplate}
              placeholders={placeholderFields}
              disableAttachment={disableAttachment}
              disableImageUpload={disableImageUpload}
              handleFileAttachments={handleFileAttachments}
              handleAttachmentsDeletion={handleAttachmentsDeletion}
              module={module}
              details={details}
              isUploading={isUploading}
              editorRef={editorRef}
              allowPlaceholders
              allowMultiple
              config={{
                htmlUntouched: true
              }}
              toolbarSticky={false}
              disableAttachment={!allowAttachments}
            />
          </ReplyTemplateContainer>

          {isBannerVisible && placeholderFields && !!placeholderFields.length && (
            <div className="m15 br-5">
              <Banner
                isClose
                title={getTranslatedText(
                  intl,
                  'ticket.details.merge.header.imp'
                )}
                status="info"
                onDismiss={() => showBanner(!isBannerVisible)}
              >
                <DisplayText element="p" size="large">
                  {getTranslatedText(
                    intl,
                    'admin.eventTrigger.emailTemplate.banner.message'
                  )}
                </DisplayText>
              </Banner>
            </div>
          )}
        </div>
        {props?.isNotification &&
          (template?.canConfigureTo || template?.canConfigureCcAndBcc) && (
            <div className="mt-40">
              <EmailSenderForm
                template={template}
                placeholders={
                  data?.getNotificationTemplateMetaInfo
                    ?.recipientsPlaceHolders || []
                }
              />
            </div>
          )}
        {ALLOW_FILTER_OPTIONS.includes(template?.id) && (
          <div className="mt-20">
            <NotificationFilterForm
              template={template}
              updateTemplate={updateTemplate}
            />
          </div>
        )}
      </div>
      <div className="full-height">
        {template?.templateType === TEMPLATE_TYPES.customTicketing && (
          <div className="btn-split flex mb-20">
            <div>
              <FormattedMessage id="admin.replytemplate.sendto" />
            </div>
            <ButtonGroup segmented>
              <Button
                outline={
                  template?.templateSubtype === TEMPLATE_SUB_TYPES.technician
                }
                onClick={() =>
                  updateTemplate({
                    templateSubtype: TEMPLATE_SUB_TYPES.technician,
                    to: [],
                    cc: [],
                    bcc: []
                  })
                }
              >
                <FormattedMessage id="admin.replytemplate.technician" />
              </Button>
              <Button
                outline={
                  template?.templateSubtype === TEMPLATE_SUB_TYPES.requester
                }
                onClick={() =>
                  updateTemplate({
                    templateSubtype: TEMPLATE_SUB_TYPES.requester,
                    to: [],
                    cc: [],
                    bcc: []
                  })
                }
              >
                <FormattedMessage id="admin.replytemplate.requester" />
              </Button>
            </ButtonGroup>
          </div>
        )}
        <div
          className={`placeholder-container-background br10 p20 ml-20 scroll full-width ${
            template?.templateType === TEMPLATE_TYPES.customTicketing
              ? 'ch-60'
              : 'full-height'
          }`}
        >
          <PlaceholderContainer
            template={template}
            data={data?.getNotificationTemplateMetaInfo}
            fields={placeholderFields}
            type={template?.templateType}
            isNewMeta={
              template?.templateType === TEMPLATE_TYPES.systemClient ||
              template?.templateType === TEMPLATE_TYPES.systemTask ||
              template?.name === 'Technician Mention'
            }
          />
        </div>
      </div>
    </div>
  );
}

export default injectIntl(withRouter(EmailTemplateEditor));
